import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import Navbar from "../components/Navbar";
import Heart from "react-heart";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Bounce, ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CarImageSlider from "./CarImageSlider";
import Footer from "../Footer";
import Swal from "sweetalert2";
import axios from "axios";
import { toTitleCase } from "../helper/formatCarName";
import { SkeletonCard } from "./SkeletonLoader";

const FilterCarData = () => {
  const navigator = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  // Toast configuration
  const optionToast = {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "colored",
    transition: Bounce,
  };

  const locationKey = location.key || "default";

  const getCachedState = () => {
    try {
      const data = sessionStorage.getItem(`carSearch_${locationKey}`);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  };

  const cachedState = useRef(getCachedState()).current;

  // State management
  const [sortOption, setSortOption] = useState(cachedState ? cachedState.sortOption : "recently_added");
  const [filters, setFilters] = useState(() => {
    if (cachedState) return cachedState.filters;
    return {
      distance: queryParams.get("distance") || 1000000,
      postcode: queryParams.get("postcode") || "",
      minPrice: queryParams.get("min_price") || 0,
      maxPrice: queryParams.get("max_price") || 1000000,
      name: queryParams.get("make") || "",
      model: queryParams.get("model") || "",
      model_varient: queryParams.get("model_variant") || "",
      body_type: queryParams.get("body_type") || "",
      fuelType: queryParams.get("fuel_type")
        ? queryParams.get("fuel_type").split(",")
        : [],
      gearbox: queryParams.get("gearbox")
        ? queryParams.get("gearbox").split(",")
        : [],
      colors: queryParams.get("colors")
        ? queryParams.get("colors").split(",")
        : [],
      minMileage: queryParams.get("min_mileage") || "",
      maxMileage: queryParams.get("max_mileage") || "",
      minYear: queryParams.get("minYear") || "",
      maxYear: queryParams.get("maxYear") || "",
      engineSize: queryParams.get("engine_size") || "",
    };
  });

  const [filteredData, setFilteredData] = useState(cachedState ? cachedState.filteredData : []);
  const [likeStatus, setLikeStatus] = useState({});
  const [isShowFilter, setIsShowFilter] = useState(true);
  const [isShowBack, setIsShowBack] = useState(false);
  const [showDiv, setShowDiv] = useState(false);
  const [loading, setLoading] = useState(false);
  const [postcode, setPostcode] = useState(cachedState ? cachedState.filters.postcode : "");
  const [currentPage, setCurrentPage] = useState(cachedState ? cachedState.currentPage : 1);
  const [pagination, setPagination] = useState(cachedState ? cachedState.pagination : null);

  // Sync state to sessionStorage whenever it changes
  useEffect(() => {
    const saveState = () => {
      const stateToSave = {
        filters,
        filteredData,
        pagination,
        currentPage,
        sortOption,
        scrollY: window.scrollY
      };
      try {
        sessionStorage.setItem(`carSearch_${locationKey}`, JSON.stringify(stateToSave));
      } catch (e) {
        // Ignore quota exceeded errors
      }
    };

    saveState();

    let timeout;
    const handleScroll = () => {
      clearTimeout(timeout);
      timeout = setTimeout(saveState, 150);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(timeout);
    };
  }, [filters, filteredData, pagination, currentPage, sortOption, locationKey]);
  // Data for dropdowns
  const [fmodelVariantData, setModelVariantsData] = useState([]);
  const [modelData, setModelData] = useState([]);
  const [makeData, setMakeData] = useState([]);
  const [grearBoxs, setGearBoxs] = useState([]);
  const [allColors, setAllColors] = useState([]);
  const [bodTypes, setBodyTypes] = useState([]);

  // Helper functions
  const getDataFromlocalStorage = (key) => {
    try {
      const serializedData = localStorage.getItem(key);
      return serializedData ? JSON.parse(serializedData) : null;
    } catch (error) {
      console.error("Error getting data from localStorage:", error);
      return null;
    }
  };

  const storedUserPreferences = getDataFromlocalStorage("userData");

  const formatEuroAmount = (amount) => {
    const parts = amount.toString().split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  };

  const convertCcToLiters = (cc) => {
    if (cc > 0) {
      return (cc / 1000).toFixed(1);
    } else {
      return 0;
    }
  };

  const showAlert = (data, id, show) => {
    if (show) {
      toast.success(data, optionToast);
      setTimeout(() => {
        navigator("/profilesetting");
      }, 1500);
    } else {
      toast.error(data, optionToast);
    }
  };

  const formatNumber = (num) => {
    if (!num) return "0";
    if (num >= 10000000) return (num / 10000000).toFixed(2) + "M";
    if (num >= 100000) return (num / 100000).toFixed(2) + "L";
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num.toString();
  };

  // Data fetching functions
  const getMake = useCallback(async () => {
    try {
      const response = await axios.get(
        window.CARLOCA_API + "/cars/make"
      );
      if (response?.data?.status) {
        setMakeData(response?.data?.data);
      }
    } catch (error) {
      console.error("An error occurred while fetching data.", error);
    }
  }, []);

  const getModal = useCallback(async (companyName) => {
    try {
      const response = await axios.post(
        window.CARLOCA_API + "/cars/model",
        { make: companyName }
      );
      if (response?.data?.status) {
        setModelData(response?.data?.data);
      }
    } catch (error) {
      console.error("An error occurred while fetching data.", error);
    }
  }, []);

  const getModalVarient = useCallback(async (model) => {
    try {
      const response = await axios.post(
        window.CARLOCA_API + "/cars/model-variant",
        { model: model }
      );
      if (response?.data?.status) {
        setModelVariantsData(response?.data?.data);
      }
    } catch (error) {
      console.error("An error occurred while fetching data.", error);
    }
  }, []);

  const getBodyType = useCallback(async (model) => {
    try {
      const response = await axios.post(
        window.CARLOCA_API + "/cars/body-type",
        { model: model }
      );
      if (response?.data?.status) {
        setBodyTypes(response?.data?.data);
      }
    } catch (error) {
      console.error("An error occurred while fetching data.", error);
    }
  }, []);

  const getGearbox = useCallback(async () => {
    try {
      const response = await axios.get(
        window.CARLOCA_API + "/cars/gearbox"
      );
      if (response?.data?.status) {
        setGearBoxs(response?.data?.data);
      }
    } catch (error) {
      console.error("An error occurred while fetching data.", error);
    }
  }, []);

  const getColors = useCallback(async () => {
    try {
      const response = await axios.get(
        window.CARLOCA_API + "/cars/colors"
      );
      if (response?.data?.status) {
        setAllColors(response?.data?.data);
      }
    } catch (error) {
      console.error("An error occurred while fetching data.", error);
    }
  }, []);

  // Filter and search functions
  const sortCars = useCallback((carsData, sortOption) => {
    if (sortOption) {
      switch (sortOption) {
        case "highest_price":
          return carsData.sort(
            (a, b) =>
              parseFloat(b.offer_price.replace(/,/g, "")) -
              parseFloat(a.offer_price.replace(/,/g, ""))
          );
        case "lowest_price":
          return carsData.sort(
            (a, b) =>
              parseFloat(a.offer_price.replace(/,/g, "")) -
              parseFloat(b.offer_price.replace(/,/g, ""))
          );
        case "mileage_high":
          return carsData.sort((a, b) => b.current_mileage - a.current_mileage);
        case "mileage_low":
          return carsData.sort((a, b) => a.current_mileage - b.current_mileage);
        case "recently_added":
          return carsData.sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          );
        case "age_newest":
          return carsData.sort((a, b) => b.year - a.year);
        case "age_oldest":
          return carsData.sort((a, b) => a.year - b.year);
        default:
          return carsData;
      }
    }
    return carsData;
  }, []);

  const searchFilterApi = useCallback(
    async (sortOpt = "", page = 1) => {
      setLoading(true);
      try {
        const searchValue = {
          postcode: filters.postcode,
          distance: filters.distance,
          min_price: filters.minPrice,
          max_price: filters.maxPrice,
          make: filters.name,
          model: filters.model,
          model_variant: filters.model_varient,
          body_type: filters.body_type,
          fuelTypeData: filters.fuelType,
          gearBoxData: filters.gearbox,
          colorData: filters.colors,
          transmission_type: filters.gearbox.join(","),
          min_mileage: filters.minMileage,
          max_mileage: filters.maxMileage,
          minyear: filters.minYear,
          maxyear: filters.maxYear,
          engine_size: filters.engineSize,
          user_id: storedUserPreferences?.id,
          page: page,
          sort_by: sortOpt || "recently_added",
        };

        const response = await axios.post(
          window.CARLOCA_API + "/cars/search2",
          searchValue
        );

        if (response?.data?.status) {
          const carsData = response?.data?.data;
          const sortedCars = sortCars(carsData, sortOpt);
          const initialLikeStatus = sortedCars.reduce((status, car) => {
            status[car.id] = car.isFav;
            return status;
          }, {});
          setLikeStatus(prev => ({ ...prev, ...initialLikeStatus }));
          if (page > 1) {
            setFilteredData(prev => [...prev, ...sortedCars]);
          } else {
            setFilteredData(sortedCars);
          }
          if (response?.data?.pagination) {
            setPagination(response.data.pagination);
            setCurrentPage(response.data.pagination.current_page);
          }
        } else {
          if (page === 1) {
            setFilteredData([]);
          }
          setPagination(null);
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error("An error occurred while fetching data.", error);
      }
    },
    [filters, sortCars, storedUserPreferences]
  );

  const handleLoadMore = useCallback(() => {
    const nextPage = currentPage + 1;
    searchFilterApi(sortOption, nextPage);
  }, [searchFilterApi, sortOption, currentPage]);

  // Event handlers
  const handleLike = useCallback(
    (cardId) => {
      if (storedUserPreferences !== null) {
        setLikeStatus((prevStatus) => ({
          ...prevStatus,
          [cardId]: !prevStatus[cardId],
        }));
        likeDislike(storedUserPreferences?.id, cardId);
      } else {
        toast.warning("Please login to add favorite car!", optionToast);
      }
    },
    [storedUserPreferences]
  );

  const likeDislike = useCallback(async (user_id, card_id) => {
    try {
      const responsed = await axios.post(
        window.CARLOCA_API + "/like-dislike",
        {
          user_id: user_id,
          car_id: card_id,
        }
      );
      if (responsed?.data?.status) {
        toast.success(responsed?.data?.message, optionToast);
      } else {
        toast.error(responsed?.data?.message, optionToast);
      }
    } catch (error) {
      console.error("An error occurred while fetching data.", error);
    }
  }, []);

  const updateCheckboxFilters = useCallback((name, value, isChecked) => {
    setFilters((prev) => {
      const currentArray = [...prev[name]];
      if (isChecked) {
        return { ...prev, [name]: [...currentArray, value] };
      } else {
        return {
          ...prev,
          [name]: currentArray.filter((item) => item !== value),
        };
      }
    });
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({
      distance: "",
      postcode: "",
      minPrice: 0,
      maxPrice: 1000000,
      name: "",
      model: "",
      model_varient: "",
      body_type: "",
      fuelType: [],
      gearbox: [],
      colors: [],
      minMileage: "",
      maxMileage: "",
      minYear: "",
      maxYear: "",
      engineSize: "",
    });
    setSortOption("");
    // Also reset checkboxes in the DOM
    document.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
      checkbox.checked = false;
    });
  }, []);

  const applyFilters = useCallback(() => {
    setCurrentPage(1);
    searchFilterApi(sortOption, 1);
    setIsShowBack(true);
  }, [searchFilterApi, sortOption]);

  const checkIfMobile = useCallback(() => {
    const mediaQuery = window.matchMedia("(max-width: 840px)");
    setIsShowFilter(!mediaQuery.matches);
  }, []);

  const backto = useCallback(() => {
    window.history.back();
  }, []);

  // Effects
  useEffect(() => {
    getMake();
    getGearbox();
    getColors();
    checkIfMobile();
    getBodyType(null);
    setPostcode(filters.postcode);
    if (filters.name) {
      getModal(filters.name);
    }
    if (filters.model) {
      getModalVarient(filters.model);
      getBodyType(filters.model);
    }
  }, [
    filters.name,
    filters.model,
    getMake,
    getGearbox,
    getColors,
    getModal,
    getModalVarient,
    getBodyType,
    checkIfMobile,
  ]);

  const isFirstMount = useRef(true);

  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      if (cachedState) {
        // If we restored from cache, skip the initial fetch and restore scroll position
        setTimeout(() => {
          window.scrollTo({ top: cachedState.scrollY || 0, behavior: "instant" });
        }, 50);
        return;
      }
    }

    const timeoutId = setTimeout(() => {
      searchFilterApi(sortOption);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [
    filters.distance,
    filters.postcode,
    filters.minPrice,
    filters.maxPrice,
    filters.name,
    filters.model,
    filters.model_varient,
    filters.body_type,
    filters.fuelType,
    filters.gearbox,
    filters.colors,
    filters.minMileage,
    filters.maxMileage,
    filters.minYear,
    filters.maxYear,
    filters.engineSize,
    sortOption,
  ]);

  // Options for dropdowns
  const startYear = 2014;
  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: currentYear - startYear + 1 },
    (_, i) => startYear + i
  );

  const mmileOptions = [];
  for (let mile = 10000; mile <= 250000; mile += 10000) {
    mmileOptions.push({
      value: mile,
      label: `${mile.toLocaleString()} Miles`,
    });
  }
  mmileOptions.push({
    value: 500000,
    label: `${(250000).toLocaleString()}+ Miles`,
  });

  const generateOptions = () => {
    const options = [];
    for (let i = 1.1; i <= 10; i += 0.1) {
      options.push(i.toFixed(1));
    }
    return options;
  };

  // State for managing open dropdown in horizontal filter bar
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const dropdownRef = useRef(null);
  const scrollRef = useRef(null);
  const loaderRef = useRef(null);

  // Auto-load observer for infinite scrolling
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && pagination?.has_more && !loading) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    const currentLoader = loaderRef.current;
    if (currentLoader) {
      observer.observe(currentLoader);
    }

    return () => {
      if (currentLoader) observer.unobserve(currentLoader);
    };
  }, [pagination?.has_more, loading, handleLoadMore]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (e.target.closest('.hf-dropdown')) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  // Scroll arrow visibility
  const updateScrollArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  const scrollChips = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction === "left" ? -200 : 200, behavior: "smooth" });
    setTimeout(updateScrollArrows, 350);
  };

  useEffect(() => {
    updateScrollArrows();
    window.addEventListener("resize", updateScrollArrows);
    return () => window.removeEventListener("resize", updateScrollArrows);
  }, [updateScrollArrows]);

  const toggleDropdown = (name, e) => {
    if (openDropdown === name) {
      setOpenDropdown(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const left = Math.min(rect.left, window.innerWidth - 220);
      setDropdownPos({ top: rect.bottom + 6, left: Math.max(8, left) });
      setOpenDropdown(name);
    }
  };

  // Count active filters
  const activeFilterCount = [
    filters.name,
    filters.model,
    filters.model_varient,
    filters.body_type,
    filters.minYear,
    filters.maxYear,
    filters.maxMileage,
    filters.engineSize,
    filters.minPrice > 0 ? filters.minPrice : "",
    filters.maxPrice < 1000000 ? filters.maxPrice : "",
    ...(filters.fuelType || []),
    ...(filters.gearbox || []),
    ...(filters.colors || []),
  ].filter(Boolean).length;

  // Helper to get price label
  const getPriceLabel = () => {
    const min = Number(filters.minPrice);
    const max = Number(filters.maxPrice);
    if (min > 0 && max < 1000000) return `£${formatEuroAmount(min)} – £${formatEuroAmount(max)}`;
    if (min > 0) return `From £${formatEuroAmount(min)}`;
    if (max < 1000000) return `Up to £${formatEuroAmount(max)}`;
    return "";
  };

  // Offcanvas all-filters content
  const allFiltersContent = (
    <div className="row smallfilter">
      <div className="col-12 mb-3">
        <label className="form-label fw-semibold mb-1" style={{ fontSize: "13px" }}>Postcode</label>
        <input type="text" className="form-control" placeholder="e.g., SW1A 1AA" value={postcode}
          onChange={(e) => { setPostcode(e.target.value); setFilters((prev) => ({ ...prev, postcode: e.target.value })); }} />
      </div>
      <div className="col-6 mb-3">
        <label className="form-label fw-semibold mb-1" style={{ fontSize: "13px" }}>Make</label>
        <select className="form-select" value={filters.name} onChange={(e) => { setFilters((prev) => ({ ...prev, name: e.target.value })); getModal(e.target.value); }}>
          <option value="">All Makes</option>
          {makeData.map((item, i) => <option key={i} value={item?.make}>{item?.make}</option>)}
        </select>
      </div>
      <div className="col-6 mb-3">
        <label className="form-label fw-semibold mb-1" style={{ fontSize: "13px" }}>Model</label>
        <select className="form-select" value={filters.model} onChange={(e) => { setFilters((prev) => ({ ...prev, model: e.target.value })); getModalVarient(e.target.value); getBodyType(e.target.value); }}>
          <option value="">All Models</option>
          {modelData.map((item, i) => <option key={i} value={item?.model}>{item?.model}</option>)}
        </select>
      </div>
      <div className="col-6 mb-3">
        <label className="form-label fw-semibold mb-1" style={{ fontSize: "13px" }}>Variant</label>
        <select className="form-select" value={filters.model_varient} onChange={(e) => setFilters((prev) => ({ ...prev, model_varient: e.target.value }))}>
          <option value="">All Variants</option>
          {fmodelVariantData.map((item, i) => <option key={i} value={item?.model_varient}>{item?.model_varient}</option>)}
        </select>
      </div>
      <div className="col-6 mb-3">
        <label className="form-label fw-semibold mb-1" style={{ fontSize: "13px" }}>Body Type</label>
        <select className="form-select" value={filters.body_type} onChange={(e) => setFilters((prev) => ({ ...prev, body_type: e.target.value }))}>
          <option value="">All Types</option>
          {bodTypes.map((item, i) => <option key={i} value={item?.body_type}>{item?.body_type}</option>)}
        </select>
      </div>
      <div className="col-6 mb-3">
        <label className="form-label fw-semibold mb-1" style={{ fontSize: "13px" }}>Min Price</label>
        <select className="form-select" value={filters.minPrice} onChange={(e) => setFilters((prev) => ({ ...prev, minPrice: e.target.value }))}>
          <option value="0">No Min</option>
          {[5000, 10000, 20000, 30000, 40000, 50000, 60000, 70000, 80000, 90000, 100000, 200000, 300000, 400000, 500000].map(v => <option key={v} value={v}>£{formatEuroAmount(v)}</option>)}
        </select>
      </div>
      <div className="col-6 mb-3">
        <label className="form-label fw-semibold mb-1" style={{ fontSize: "13px" }}>Max Price</label>
        <select className="form-select" value={filters.maxPrice} onChange={(e) => setFilters((prev) => ({ ...prev, maxPrice: e.target.value }))}>
          <option value="1000000">No Max</option>
          {[5000, 10000, 20000, 30000, 40000, 50000, 60000, 70000, 80000, 90000, 100000, 200000, 300000, 400000, 500000].map(v => <option key={v} value={v}>£{formatEuroAmount(v)}</option>)}
        </select>
      </div>
      <div className="col-6 mb-3">
        <label className="form-label fw-semibold mb-1" style={{ fontSize: "13px" }}>Max Mileage</label>
        <select className="form-select" value={filters.maxMileage} onChange={(e) => setFilters((prev) => ({ ...prev, maxMileage: e.target.value }))}>
          <option value="">Any</option>
          {mmileOptions.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </div>
      <div className="col-6 mb-3">
        <label className="form-label fw-semibold mb-1" style={{ fontSize: "13px" }}>Distance</label>
        <select className="form-select" value={filters.distance} onChange={(e) => setFilters((prev) => ({ ...prev, distance: e.target.value }))}>
          <option value="1000000">Nationwide</option>
          {[10, 20, 30, 50, 100, 200].map(d => <option key={d} value={d}>{d} miles</option>)}
        </select>
      </div>
      <div className="col-6 mb-3">
        <label className="form-label fw-semibold mb-1" style={{ fontSize: "13px" }}>Min Year</label>
        <select className="form-select" value={filters.minYear} onChange={(e) => setFilters((prev) => ({ ...prev, minYear: e.target.value }))}>
          <option value="">Any</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      <div className="col-6 mb-3">
        <label className="form-label fw-semibold mb-1" style={{ fontSize: "13px" }}>Max Year</label>
        <select className="form-select" value={filters.maxYear} onChange={(e) => setFilters((prev) => ({ ...prev, maxYear: e.target.value }))}>
          <option value="">Any</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      <div className="col-12 mb-3">
        <h6 className="fw-semibold mb-2" style={{ fontSize: "13px" }}>Gearbox</h6>
        <div className="d-flex flex-wrap gap-2">
          {grearBoxs.map((item, i) => (
            <div key={i} className="form-check d-flex align-items-center">
              <input className="form-check-input me-1" type="checkbox" onChange={(e) => updateCheckboxFilters("gearbox", item?.transmission_type, e.target.checked)}
                value={item?.transmission_type} checked={filters.gearbox.includes(item?.transmission_type)} />
              <label className="form-check-label text-capitalize mb-0" style={{ fontSize: "12px" }}>{item?.transmission_type}</label>
            </div>
          ))}
        </div>
      </div>
      <div className="col-12 mb-3">
        <h6 className="fw-semibold mb-2" style={{ fontSize: "13px" }}>Fuel Type</h6>
        <div className="row">
          {["petrol", "diesel", "bi fuel", "electric", "diesel hybrid", "petrol hybrid"].map((ft, i) => (
            <div key={i} className="col-6 mb-2">
              <div className="form-check d-flex align-items-center">
                <input className="form-check-input me-1" type="checkbox" onChange={(e) => updateCheckboxFilters("fuelType", ft, e.target.checked)}
                  value={ft} checked={filters.fuelType.includes(ft)} />
                <label className="form-check-label text-capitalize mb-0" style={{ fontSize: "12px" }}>{ft}</label>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="col-12 mb-3">
        <h6 className="fw-semibold mb-2" style={{ fontSize: "13px" }}>Engine Size</h6>
        <select className="form-select" value={filters.engineSize} onChange={(e) => setFilters((prev) => ({ ...prev, engineSize: e.target.value }))}>
          <option value="">Any</option>
          {generateOptions().map((es) => <option key={es} value={es}>{es}L</option>)}
        </select>
      </div>
      <div className="col-12 mb-3">
        <h6 className="fw-semibold mb-2" style={{ fontSize: "13px" }}>Colour</h6>
        <div className="row">
          {allColors.map((item, i) => (
            <div key={i} className="col-4 mb-2">
              <div className="form-check d-flex align-items-center">
                <input className="form-check-input me-1" type="checkbox" onChange={(e) => updateCheckboxFilters("colors", item?.color, e.target.checked)}
                  value={item?.color} checked={filters.colors.includes(item?.color)} />
                <label className="form-check-label text-capitalize mb-0" style={{ fontSize: "12px" }}>{item?.color}</label>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="col-12 d-flex gap-2 mt-2">
        <button onClick={() => { resetFilters(); const el = document.getElementById("allFiltersOffcanvas"); if (el) { const bs = window.bootstrap?.Offcanvas?.getInstance(el); if (bs) bs.hide(); } }} className="btn btn-outline-primary flex-grow-1 fw-semibold" style={{ fontSize: "13px" }}>
          <i className="fa-solid fa-rotate-right me-1"></i> Reset
        </button>
        <button onClick={() => { applyFilters(); const el = document.getElementById("allFiltersOffcanvas"); if (el) { const bs = window.bootstrap?.Offcanvas?.getInstance(el); if (bs) bs.hide(); } }} className="btn btn-primary flex-grow-1 fw-semibold" style={{ fontSize: "13px" }}>
          <i className="fa-solid fa-magnifying-glass me-1"></i> Apply
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Navbar />
      <div className="new_page pb-4" style={{ minHeight: "100vh" }}>

        {/* ====== ALL FILTERS OFFCANVAS ====== */}
        <div className="offcanvas offcanvas-end" tabIndex="-1" id="allFiltersOffcanvas" aria-labelledby="allFiltersLabel" style={{ width: "340px", maxWidth: "90vw" }} data-bs-scroll="false">
          <div className="offcanvas-header py-3" style={{ borderBottom: "2px solid #00889c", background: "#f8fafb" }}>
            <h5 className="offcanvas-title fw-bold mb-0" id="allFiltersLabel" style={{ color: "#00889c", fontSize: "16px" }}>
              <i className="fa-solid fa-sliders me-2"></i>All Filters
              {activeFilterCount > 0 && <span className="badge rounded-pill ms-2" style={{ backgroundColor: "#00889c", fontSize: "11px" }}>{activeFilterCount}</span>}
            </h5>
            <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
          </div>
          <div className="offcanvas-body d-flex justify-content-center pt-3" style={{ overflowY: "auto" }}>{allFiltersContent}</div>
        </div>

        {/* ====== MAIN LAYOUT ====== */}
        <div className="container-fluid px-lg-4 px-2" style={{ paddingTop: "75px" }}>

          {/* ====== HORIZONTAL FILTER BAR (sticky) ====== */}
          <div className="hf-bar" ref={dropdownRef}>
            {canScrollLeft && (
              <button className="hf-scroll-btn hf-scroll-left" onClick={() => scrollChips("left")} aria-label="Scroll left">
                <i className="fa-solid fa-chevron-left"></i>
              </button>
            )}
            <div className="hf-bar-inner" ref={scrollRef} onScroll={() => { setOpenDropdown(null); updateScrollArrows(); }}>
              {/* All Filters button */}
              <div className="hf-item">
                <button className="hf-chip hf-chip-all" data-bs-toggle="offcanvas" data-bs-target="#allFiltersOffcanvas" onClick={() => setOpenDropdown(null)}>
                  <i className="fa-solid fa-sliders me-1"></i> All Filters
                  {activeFilterCount > 0 && <span className="hf-count-badge">{activeFilterCount}</span>}
                </button>
              </div>

              {/* Reset */}
              <div className="hf-item">
                <button className="hf-chip hf-chip-reset" onClick={resetFilters}>
                  <i className="fa-solid fa-rotate-right me-1"></i> Reset
                </button>
              </div>

              {/* Postcode input */}
              <div className="hf-item hf-postcode-item">
                <div className="hf-postcode-wrap">
                  <i className="fa-solid fa-location-dot" style={{ color: "#00889c", fontSize: "13px" }}></i>
                  <input
                    type="text"
                    className="hf-postcode-input"
                    placeholder="Postcode"
                    value={postcode}
                    onChange={(e) => { setPostcode(e.target.value); setFilters((prev) => ({ ...prev, postcode: e.target.value })); }}
                  />
                </div>
              </div>

              {/* Make chip */}
              <div className="hf-item">
                <button className={`hf-chip ${filters.name ? "hf-chip-active" : ""}`} onClick={(e) => toggleDropdown("make", e)}>
                  {filters.name || "Make"} <i className={`fa-solid fa-chevron-${openDropdown === "make" ? "up" : "down"} ms-1`} style={{ fontSize: "10px" }}></i>
                </button>
                {openDropdown === "make" && document.body && createPortal(
                  <div className="hf-dropdown" style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, zIndex: 1060 }}>
                    <div className="hf-dropdown-item" onClick={() => { setFilters(p => ({ ...p, name: "" })); setOpenDropdown(null); }}>All Makes</div>
                    {makeData.map((item, i) => (
                      <div key={i} className={`hf-dropdown-item ${filters.name === item?.make ? "hf-dropdown-item-active" : ""}`}
                        onClick={() => { setFilters(p => ({ ...p, name: item?.make })); getModal(item?.make); setOpenDropdown(null); }}>{item?.make}</div>
                    ))}
                  </div>
                  , document.body)}
              </div>

              {/* Model chip */}
              <div className="hf-item">
                <button className={`hf-chip ${filters.model ? "hf-chip-active" : ""}`} onClick={(e) => toggleDropdown("model", e)}>
                  {filters.model || "Model"} <i className={`fa-solid fa-chevron-${openDropdown === "model" ? "up" : "down"} ms-1`} style={{ fontSize: "10px" }}></i>
                </button>
                {openDropdown === "model" && document.body && createPortal(
                  <div className="hf-dropdown" style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, zIndex: 1060 }}>
                    <div className="hf-dropdown-item" onClick={() => { setFilters(p => ({ ...p, model: "" })); setOpenDropdown(null); }}>All Models</div>
                    {modelData.map((item, i) => (
                      <div key={i} className={`hf-dropdown-item ${filters.model === item?.model ? "hf-dropdown-item-active" : ""}`}
                        onClick={() => { setFilters(p => ({ ...p, model: item?.model })); getModalVarient(item?.model); getBodyType(item?.model); setOpenDropdown(null); }}>{item?.model}</div>
                    ))}
                  </div>
                  , document.body)}
              </div>

              {/* Budget chip */}
              <div className="hf-item">
                <button className={`hf-chip ${getPriceLabel() ? "hf-chip-active" : ""}`} onClick={(e) => toggleDropdown("budget", e)}>
                  {getPriceLabel() || "Price"} <i className={`fa-solid fa-chevron-${openDropdown === "budget" ? "up" : "down"} ms-1`} style={{ fontSize: "10px" }}></i>
                </button>
                {openDropdown === "budget" && document.body && createPortal(
                  <div className="hf-dropdown hf-dropdown-wide" style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, zIndex: 1060 }}>
                    <div className="px-3 py-2">
                      <label className="fw-semibold mb-1 d-block" style={{ fontSize: "12px", color: "#555" }}>Min Price</label>
                      <select className="form-select form-select-sm mb-2" value={filters.minPrice}
                        onChange={(e) => setFilters(p => ({ ...p, minPrice: e.target.value }))}>
                        <option value="0">No Min</option>
                        {[5000, 10000, 20000, 30000, 40000, 50000, 75000, 100000, 200000, 500000].map(v => <option key={v} value={v}>£{formatEuroAmount(v)}</option>)}
                      </select>
                      <label className="fw-semibold mb-1 d-block" style={{ fontSize: "12px", color: "#555" }}>Max Price</label>
                      <select className="form-select form-select-sm" value={filters.maxPrice}
                        onChange={(e) => setFilters(p => ({ ...p, maxPrice: e.target.value }))}>
                        <option value="1000000">No Max</option>
                        {[5000, 10000, 20000, 30000, 40000, 50000, 75000, 100000, 200000, 500000].map(v => <option key={v} value={v}>£{formatEuroAmount(v)}</option>)}
                      </select>
                    </div>
                  </div>
                  , document.body)}
              </div>

              {/* Year chip */}
              <div className="hf-item">
                <button className={`hf-chip ${filters.minYear || filters.maxYear ? "hf-chip-active" : ""}`} onClick={(e) => toggleDropdown("year", e)}>
                  {filters.minYear && filters.maxYear ? `${filters.minYear} – ${filters.maxYear}` : filters.minYear ? `From ${filters.minYear}` : filters.maxYear ? `Up to ${filters.maxYear}` : "Year"}
                  {" "}<i className={`fa-solid fa-chevron-${openDropdown === "year" ? "up" : "down"} ms-1`} style={{ fontSize: "10px" }}></i>
                </button>
                {openDropdown === "year" && document.body && createPortal(
                  <div className="hf-dropdown hf-dropdown-wide" style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, zIndex: 1060 }}>
                    <div className="px-3 py-2">
                      <label className="fw-semibold mb-1 d-block" style={{ fontSize: "12px", color: "#555" }}>Min Year</label>
                      <select className="form-select form-select-sm mb-2" value={filters.minYear}
                        onChange={(e) => setFilters(p => ({ ...p, minYear: e.target.value }))}>
                        <option value="">Any</option>
                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                      <label className="fw-semibold mb-1 d-block" style={{ fontSize: "12px", color: "#555" }}>Max Year</label>
                      <select className="form-select form-select-sm" value={filters.maxYear}
                        onChange={(e) => setFilters(p => ({ ...p, maxYear: e.target.value }))}>
                        <option value="">Any</option>
                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                  </div>
                  , document.body)}
              </div>

              {/* Body Type chip */}
              <div className="hf-item">
                <button className={`hf-chip ${filters.body_type ? "hf-chip-active" : ""}`} onClick={(e) => toggleDropdown("body", e)}>
                  {filters.body_type || "Body Type"} <i className={`fa-solid fa-chevron-${openDropdown === "body" ? "up" : "down"} ms-1`} style={{ fontSize: "10px" }}></i>
                </button>
                {openDropdown === "body" && document.body && createPortal(
                  <div className="hf-dropdown" style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, zIndex: 1060 }}>
                    <div className="hf-dropdown-item" onClick={() => { setFilters(p => ({ ...p, body_type: "" })); setOpenDropdown(null); }}>All Types</div>
                    {bodTypes.map((item, i) => (
                      <div key={i} className={`hf-dropdown-item ${filters.body_type === item?.body_type ? "hf-dropdown-item-active" : ""}`}
                        onClick={() => { setFilters(p => ({ ...p, body_type: item?.body_type })); setOpenDropdown(null); }}>{item?.body_type}</div>
                    ))}
                  </div>
                  , document.body)}
              </div>

              {/* Fuel Type chip */}
              <div className="hf-item">
                <button className={`hf-chip ${filters.fuelType.length > 0 ? "hf-chip-active" : ""}`} onClick={(e) => toggleDropdown("fuel", e)}>
                  {filters.fuelType.length > 0 ? `Fuel (${filters.fuelType.length})` : "Fuel Type"} <i className={`fa-solid fa-chevron-${openDropdown === "fuel" ? "up" : "down"} ms-1`} style={{ fontSize: "10px" }}></i>
                </button>
                {openDropdown === "fuel" && document.body && createPortal(
                  <div className="hf-dropdown" style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, zIndex: 1060 }}>
                    {["petrol", "diesel", "bi fuel", "electric", "diesel hybrid", "petrol hybrid"].map((ft, i) => (
                      <label key={i} className="hf-dropdown-check">
                        <input type="checkbox" checked={filters.fuelType.includes(ft)} onChange={(e) => updateCheckboxFilters("fuelType", ft, e.target.checked)} />
                        <span className="text-capitalize">{ft}</span>
                      </label>
                    ))}
                  </div>
                  , document.body)}
              </div>

              {/* Gearbox chip */}
              <div className="hf-item">
                <button className={`hf-chip ${filters.gearbox.length > 0 ? "hf-chip-active" : ""}`} onClick={(e) => toggleDropdown("gearbox", e)}>
                  {filters.gearbox.length > 0 ? `Gearbox (${filters.gearbox.length})` : "Gearbox"} <i className={`fa-solid fa-chevron-${openDropdown === "gearbox" ? "up" : "down"} ms-1`} style={{ fontSize: "10px" }}></i>
                </button>
                {openDropdown === "gearbox" && document.body && createPortal(
                  <div className="hf-dropdown" style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, zIndex: 1060 }}>
                    {grearBoxs.map((item, i) => (
                      <label key={i} className="hf-dropdown-check">
                        <input type="checkbox" checked={filters.gearbox.includes(item?.transmission_type)} onChange={(e) => updateCheckboxFilters("gearbox", item?.transmission_type, e.target.checked)} />
                        <span className="text-capitalize">{item?.transmission_type}</span>
                      </label>
                    ))}
                  </div>
                  , document.body)}
              </div>

              {/* Distance chip */}
              <div className="hf-item">
                <button className={`hf-chip ${filters.distance && Number(filters.distance) !== 1000000 ? "hf-chip-active" : ""}`} onClick={(e) => toggleDropdown("distance", e)}>
                  {filters.distance && Number(filters.distance) !== 1000000 ? `${filters.distance} mi` : "Distance"} <i className={`fa-solid fa-chevron-${openDropdown === "distance" ? "up" : "down"} ms-1`} style={{ fontSize: "10px" }}></i>
                </button>
                {openDropdown === "distance" && document.body && createPortal(
                  <div className="hf-dropdown" style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, zIndex: 1060 }}>
                    <div className="hf-dropdown-item" onClick={() => { setFilters(p => ({ ...p, distance: 1000000 })); setOpenDropdown(null); }}>Nationwide</div>
                    {[10, 20, 30, 50, 100, 200].map(d => (
                      <div key={d} className={`hf-dropdown-item ${Number(filters.distance) === d ? "hf-dropdown-item-active" : ""}`}
                        onClick={() => { setFilters(p => ({ ...p, distance: d })); setOpenDropdown(null); }}>{d} miles</div>
                    ))}
                  </div>
                  , document.body)}
              </div>
            </div>
            {canScrollRight && (
              <button className="hf-scroll-btn hf-scroll-right" onClick={() => scrollChips("right")} aria-label="Scroll right">
                <i className="fa-solid fa-chevron-right"></i>
              </button>
            )}
          </div>

          {/* ====== RESULTS HEADER ====== */}
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 mt-3">
            <div className="d-flex align-items-center gap-2">
              <h6 className="mb-0 fw-bold" style={{ fontSize: "15px", color: "#222" }}>
                {loading ? (
                  <span className="text-muted">Searching...</span>
                ) : filteredData.length > 0 ? (
                  <>{pagination?.total || filteredData.length} car{(pagination?.total || filteredData.length) !== 1 ? "s" : ""} found</>
                ) : "No results"}
              </h6>
            </div>
            <div className="d-flex align-items-center gap-2">
              <select
                className="form-select form-select-sm"
                style={{ width: "auto", minWidth: "145px", borderRadius: "8px", borderColor: "#00889c", fontSize: "13px" }}
                value={sortOption}
                onChange={(e) => { setSortOption(e.target.value); setCurrentPage(1); searchFilterApi(e.target.value, 1); }}
              >
                <option value="">Sort by</option>
                <option value="highest_price">Highest Price</option>
                <option value="lowest_price">Lowest Price</option>
                <option value="mileage_low">Mileage Low</option>
                <option value="mileage_high">Mileage High</option>
                <option value="recently_added">Recently Added</option>
                <option value="age_newest">Newest First</option>
                <option value="age_oldest">Oldest First</option>
              </select>
            </div>
          </div>

          {/* ====== ACTIVE FILTER PILLS ====== */}
          {activeFilterCount > 0 && (
            <div className="d-flex flex-wrap gap-2 mb-3">
              {filters.name && <span className="filter-pill">{filters.name}<i className="fa fa-xmark ms-1" onClick={() => setFilters(p => ({ ...p, name: "" }))}></i></span>}
              {filters.model && <span className="filter-pill">{filters.model}<i className="fa fa-xmark ms-1" onClick={() => setFilters(p => ({ ...p, model: "" }))}></i></span>}
              {filters.body_type && <span className="filter-pill">{filters.body_type}<i className="fa fa-xmark ms-1" onClick={() => setFilters(p => ({ ...p, body_type: "" }))}></i></span>}
              {getPriceLabel() && <span className="filter-pill">{getPriceLabel()}<i className="fa fa-xmark ms-1" onClick={() => setFilters(p => ({ ...p, minPrice: 0, maxPrice: 1000000 }))}></i></span>}
              {(filters.minYear || filters.maxYear) && <span className="filter-pill">{filters.minYear || "?"} – {filters.maxYear || "?"}<i className="fa fa-xmark ms-1" onClick={() => setFilters(p => ({ ...p, minYear: "", maxYear: "" }))}></i></span>}
              {filters.fuelType.map(f => <span key={f} className="filter-pill text-capitalize">{f}<i className="fa fa-xmark ms-1" onClick={() => updateCheckboxFilters("fuelType", f, false)}></i></span>)}
              {filters.gearbox.map(g => <span key={g} className="filter-pill text-capitalize">{g}<i className="fa fa-xmark ms-1" onClick={() => updateCheckboxFilters("gearbox", g, false)}></i></span>)}
              {filters.colors.map(c => <span key={c} className="filter-pill text-capitalize">{c}<i className="fa fa-xmark ms-1" onClick={() => updateCheckboxFilters("colors", c, false)}></i></span>)}
              <button className="btn btn-link p-0 text-decoration-none" style={{ color: "#00889c", fontSize: "12px", fontWeight: 600 }} onClick={resetFilters}>Clear all</button>
            </div>
          )}

          {/* ====== LOADING ====== */}
          {loading && (
            <div className="row g-3">
              <SkeletonCard count={8} />
            </div>
          )}

          {/* ====== CAR GRID ====== */}
          {!loading && filteredData && filteredData.length > 0 ? (
            <div className="row g-3">
              {filteredData.map((car) => (
                <div key={car.id} className="col-xxl-3 col-xl-3 col-lg-4 col-md-4 col-sm-6 col-6">
                  <div
                    className="card car-result-card h-100 border-0 shadow-sm position-relative"
                    onClick={(e) => { if (!e.target.closest(".heart-icon, .dealer-link, .swiper-pagination")) navigator(`/cardetails?car=${car?.slug}`); }}
                    style={{ cursor: "pointer", borderRadius: "10px", overflow: "hidden" }}
                  >
                    {/* Image */}
                    <div className="car-result-img w-100" style={{ display: "flex", aspectRatio: "4/3", backgroundColor: "#111", position: "relative", overflow: "hidden" }}>
                      <CarImageSlider car={car} />
                      {/* Heart */}
                      <div className="heart-icon" style={{ position: "absolute", top: "8px", left: "8px", zIndex: 4, height: "24px", width: "24px" }}>
                        <Heart isActive={likeStatus[car.id]} onClick={() => handleLike(car.id)} animationTrigger="both"
                          inactiveColor="rgba(255,255,255,0.9)" activeColor="#00889C" animationDuration={0.2} />
                      </div>
                      {/* Year badge */}
                      <span className="position-absolute badge" style={{ bottom: "8px", left: "8px", backgroundColor: "rgba(0,136,156,0.85)", fontSize: "11px", fontWeight: 600, borderRadius: "6px", padding: "4px 8px", zIndex: 4 }}>
                        {car.year}
                      </span>
                    </div>

                    {/* Body */}
                    <div className="card-body p-3 d-flex flex-column">
                      {car?.owner?.city && (
                        <p className="mb-1 text-truncate" style={{ fontSize: "10px", color: "#6b7280", lineHeight: 1.3, display: "flex", alignItems: "center", gap: "3px" }}>
                          <i className="fa fa-location-dot" style={{ color: "#00889c", fontSize: "10px" }}></i>
                          {car?.owner?.city}
                        </p>
                      )}
                      <h6 className="fw-bold mb-0 text-truncate" style={{ fontSize: "14px", color: "#1a1a2e", lineHeight: 1.3 }}>
                        {toTitleCase(car.make)} {toTitleCase(car.model)}
                      </h6>
                      {car.model_varient && (
                        <p className="text-muted text-truncate mb-0" style={{ fontSize: "11px", lineHeight: 1.3 }}>{car.model_varient}</p>
                      )}
                      {car?.owner?.role !== "dealer" ? (
                        <p className="mb-2 fw-bold mt-1" style={{ color: "#00889c", fontSize: "15px", lineHeight: 1.2 }}>
                          £{formatEuroAmount(car.min_price)} <span style={{ color: "#aaa", fontWeight: 400 }}>–</span> £{formatEuroAmount(car.max_price)}
                        </p>
                      ) : (
                        <p className="mb-2 fw-bold mt-1" style={{ color: "#00889c", fontSize: "15px", lineHeight: 1.2 }}>
                          £{formatEuroAmount(car.offer_price)}
                        </p>
                      )}
                      {(() => {
                        let parsedBatteryCapacity = null;
                        let parsedElectricRange = null;
                        if (car?.carData && typeof car.carData === "string") {
                          try {
                            const parsedData = JSON.parse(car.carData);
                            const powertrain = parsedData?.Results?.ModelDetails?.Powertrain;
                            if (powertrain) {
                              parsedBatteryCapacity = powertrain.TotalCapacityKwh || powertrain?.EvDetails?.TechnicalDetails?.BatteryDetailsList?.[0]?.TotalCapacityKwh;
                              parsedElectricRange = powertrain.CombinedRangeMiles || powertrain.ElectricRangeMiles || powertrain?.EvDetails?.Performance?.RangeFigures?.RangeTestCycleList?.[0]?.CombinedRangeMiles;
                            }
                          } catch (e) { }
                        }
                        const batteryCapacity = car?.battery_capacity || parsedBatteryCapacity;
                        const electricRange = car?.electric_range || parsedElectricRange;
                        return (
                          <div className="car-card-specs-grid mb-2">
                            <span className="car-spec-badge"><i className="fa-solid fa-gears me-1" style={{ color: "#00889c" }}></i>{car.transmission_type}</span>
                            <span className="car-spec-badge"><i className="fa-solid fa-road me-1" style={{ color: "#00889c" }}></i>{formatNumber(car.current_mileage)}</span>
                            <span className="car-spec-badge"><i className="fa-solid fa-gas-pump me-1" style={{ color: "#00889c" }}></i>{car.fuel_type}</span>
                            {car.engine_capacity > 0 && car.fuel_type?.toLowerCase() !== "electric" && <span className="car-spec-badge"><i className="fa-solid fa-gauge-high me-1" style={{ color: "#00889c" }}></i>{convertCcToLiters(car.engine_capacity)}L</span>}
                            {batteryCapacity && <span className="car-spec-badge"><i className="fa-solid fa-battery-full me-1" style={{ color: "#00889c" }}></i>{batteryCapacity} kWh</span>}
                            {electricRange && <span className="car-spec-badge"><i className="fa-solid fa-plug me-1" style={{ color: "#00889c" }}></i>{electricRange} mi</span>}
                          </div>
                        );
                      })()}
                      <div className="car-card-footer-section mt-auto">
                        {car?.owner?.role === "dealer" ? (
                          <Link to="/dealer/store" state={{ owner_id: car?.owner?.id }} className="d-flex align-items-center dealer-link text-decoration-none flex-grow-1" onClick={(e) => e.stopPropagation()}>
                            <div style={{ height: "1.6rem", width: "1.6rem", border: "1.5px solid #ddd", borderRadius: "50%", overflow: "hidden" }} className="me-2 flex-shrink-0">
                              <img src={car?.owner?.profile ? `${window.CARLOCA_UPLOADS}/users/${car?.owner?.profile}` : `${window.CARLOCA_UPLOADS}/user.jpg`}
                                alt="" style={{ height: "100%", width: "100%", objectFit: "cover" }} />
                            </div>
                            <span className="text-capitalize text-truncate" style={{ color: "#00889c", fontWeight: 600, fontSize: "11px" }}>{car?.owner?.dealerName || car?.owner?.first_name || "Dealer"}</span>
                          </Link>
                        ) : (
                          <div className="d-flex align-items-center flex-grow-1">
                            <span style={{ color: "#00889c", fontWeight: 600, fontSize: "11px" }}>Private Seller</span>
                          </div>
                        )}
                        <i className="fa-solid fa-chevron-right car-card-arrow"></i>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : !loading ? (
            <div className="text-center py-5">
              <div style={{ fontSize: "4rem", color: "#ccc" }}><i className="fa-solid fa-car-side"></i></div>
              <h5 className="fw-semibold mt-3" style={{ color: "#555" }}>No cars found</h5>
              <p className="text-muted" style={{ fontSize: "14px" }}>Try adjusting your filters or search criteria</p>
              <button onClick={resetFilters} className="btn btn-outline-primary btn-sm mt-2" style={{ borderRadius: "8px" }}>
                <i className="fa-solid fa-rotate-right me-1"></i> Reset Filters
              </button>
            </div>
          ) : null}

          {/* ====== AUTO LOAD OBSERVER ====== */}
          {pagination?.has_more && (
            <div ref={loaderRef} className="d-flex justify-content-center mt-4 mb-2">
              {loading && (
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
      <Footer />
      <ToastContainer />
    </>
  );
};

export default FilterCarData;